/* import __COLOCATED_TEMPLATE__ from './sales-development.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import { hasFeature } from 'embercom/helpers/has-feature';
//@ts-ignore no type declaration available for predicates
import PredicateGroup from 'predicates/models/predicate-group';

type WorkflowParams = Record<string, unknown> | undefined | null;

interface Signature {
  Args: {
    showPreview?: boolean;
  };
  Blocks: {
    default?: [];
  };
  Element: HTMLDivElement;
}

interface Predicate {
  type?: string;
  predicates?: unknown[];
  [key: string]: unknown;
}

export default class SalesDevelopment extends Component<Signature> {
  @service declare store: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;

  @tracked _showPreview = false;
  @tracked showWorkflowSideSheet = false;
  @tracked selectedRoutingOutcomeIdentifier = 'qualified';
  @tracked selectedRoutingOutcomeTitle?: string;
  @tracked workflowParams?: WorkflowParams;
  @tracked showPredicatesEditorSideSheet = false;
  @tracked routingOutcomeId?: string;
  @tracked predicateGroup?: $TSFixMe;
  @tracked conditionId?: string;
  @tracked routeTitle?: string;
  @tracked conditionTitle?: string;
  @tracked predicatesMode: 'add' | 'edit' = 'edit';

  get useReactPreview(): boolean {
    return hasFeature('react-preview-panel-sdr', this.appService);
  }

  get showPreview(): boolean {
    return this.args.showPreview ?? this._showPreview;
  }

  constructor(owner: unknown, args: {}) {
    super(owner, args);
    let unsubscribePreview = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-sdr:preview:open',
      () => {
        this._showPreview = true;
      },
    );

    let unsubscribePreviewClose = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-sdr:preview:close',
      () => {
        this._showPreview = false;
      },
    );

    let unsubscribeWorkflowSheet = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-sdr:workflow-side-sheet:open',
      (data?: {
        routingOutcomeIdentifier?: string;
        routingOutcomeTitle?: string;
        workflowParams?: WorkflowParams;
      }) => {
        this.showWorkflowSideSheet = true;
        if (data?.routingOutcomeIdentifier) {
          this.selectedRoutingOutcomeIdentifier = data.routingOutcomeIdentifier;
        }
        this.selectedRoutingOutcomeTitle = data?.routingOutcomeTitle;
        this.workflowParams = data?.workflowParams;
      },
    );

    let unsubscribePredicates = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-sdr:predicates:open',
      async (data) => {
        let eventData = data as {
          routingOutcomeId?: string;
          predicates?: unknown[];
          conditionId?: string;
          routeTitle?: string;
          conditionTitle?: string;
          mode?: 'add' | 'edit';
        };

        this.routingOutcomeId = eventData.routingOutcomeId;
        this.conditionId = eventData.conditionId;
        this.routeTitle = eventData.routeTitle;
        this.conditionTitle = eventData.conditionTitle;
        this.predicatesMode = eventData.mode || 'edit';

        // Clear predicateGroup to show loading state and open the side sheet immediately
        this.predicateGroup = undefined;
        this.showPredicatesEditorSideSheet = true;

        // Reload attributes from the server to ensure any newly created attributes are in the store
        // This fixes a bug where predicates with new attributes would display without
        // the attribute name (e.g., "is greater than 10" instead of "fin_sdr_company_size is greater than 10")
        await Promise.all([
          this.store.findAll('conversation-attributes/descriptor'),
          // This is the easiest way to get all the latest user and company attributes in the store
          this.appService.app.reload(),
        ]);

        let allPredicates: unknown[] = this.normalizePredicates(eventData.predicates || []);

        this.predicateGroup = this.store.createFragment('predicates/predicate-group', {
          predicates: PredicateGroup.convertRawPredicates(this.store, allPredicates),
        });
        this.predicateGroup.switchLogicalMode('or'); // very important to set the logical mode to or
      },
    );

    let unsubscribeConfigSaved = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-sdr:config:saved',
      () => {
        this.refreshPreview();
      },
    );

    registerDestructor(this, () => {
      unsubscribePreview();
      unsubscribePreviewClose();
      unsubscribeWorkflowSheet();
      unsubscribePredicates();
      unsubscribeConfigSaved();
    });
  }

  get isWorkflowSideSheetOpen() {
    return this.showWorkflowSideSheet;
  }

  get routingOutcomeIdentifier() {
    return this.selectedRoutingOutcomeIdentifier;
  }

  get routingOutcomeTitle() {
    return this.selectedRoutingOutcomeTitle;
  }

  get isPredicatesEditorSideSheetOpen() {
    return this.showPredicatesEditorSideSheet;
  }

  @action closePreview() {
    this._showPreview = false;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:preview:close');
  }

  @action closeWorkflowSideSheet() {
    this.showWorkflowSideSheet = false;
    this.workflowParams = undefined;
    this.selectedRoutingOutcomeTitle = undefined;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:workflow-side-sheet:close');
  }

  @action closePredicatesEditorSideSheet() {
    this.showPredicatesEditorSideSheet = false;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:predicates:closed');
  }

  @action refreshPreview() {
    if (!this.showPreview) {
      return;
    }

    // Find the iframe and send it a message to reload
    let iframe = window.document.querySelector(
      '#hosted-messenger-unified-preview',
    ) as HTMLIFrameElement;

    if (iframe && iframe.contentWindow) {
      let payload = {
        type: 'show-new-conversation',
      };
      iframe.contentWindow.postMessage(JSON.stringify(payload), '*');
    }
  }

  /**
   * Normalizes incoming predicates to ensure we never have a top-level AND.
   *
   * Allowed structures:
   * 1. Flat list of predicates (implicitly AND between them)
   * 2. Top-level OR with predicate groups (each group has AND inside)
   *
   * If we detect a top-level AND (single item with type:"and"), we wrap it
   * in an OR structure to make it a single predicate group.
   */
  private normalizePredicates(predicates: unknown[]): unknown[] {
    if (!Array.isArray(predicates) || predicates.length === 0) {
      return predicates;
    }

    // Check if we have a single top-level AND
    if (
      predicates.length === 1 &&
      typeof predicates[0] === 'object' &&
      predicates[0] !== null &&
      (predicates[0] as Predicate).type === 'and'
    ) {
      // Wrap the AND group in an OR structure
      return [
        {
          type: 'or',
          predicates: predicates as Predicate[],
        },
      ];
    }

    return predicates;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::SalesDevelopment': typeof SalesDevelopment;
    'ai-agent/sales-development': typeof SalesDevelopment;
  }
}
